import CircularProgress from '@material-ui/core/CircularProgress';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import PropTypes from 'prop-types';
import React, { useLayoutEffect, useState } from 'react';
import { Redirect, Route, Switch, useLocation, useParams } from 'react-router-dom';
import ActivityPage from '../activity';
import BillingPage from '../billing';
import { useUserAuthInfo } from '../components/auth';
import Header from '../components/Header';
import {
  ACTIVITY_PATH,
  BILLING_CONTACT_PATH,
  BILLING_PATH,
  DOCUMENTS_PATH,
  HOME_ENERGY_REPORT_CONTACT_PATH,
  HOME_ENERGY_REPORT_LIST_PATH,
  HOME_ENERGY_REPORT_PATH,
  LD_NOTES_PATH,
  pathToRoute,
  PAYMENT_PATH,
  SERVICE_CONTRACT_PATH,
  SITE_DETAILS_PATH,
  SYSTEM_PATH,
} from '../constants/routes';
import DocumentsPage from '../documents';
import HomeEnergyReportPage from '../homeEnergyReport';
import HomeEnergyReportListPage from '../homeEnergyReport/HomeEnergyReportListPage';
import useSearchParams from '../hooks/useSearchParams';
import LDNotesPage from '../ldNotes';
import PaymentPage from '../payment';
import ServiceContractPage from '../serviceContract';
import SiteDetailsPage from '../siteDetails';
import SystemPage from '../system';
import { useHideFlags, useDepWarnFlags } from '../hooks/useGetFeatureFlag';
import InfoHubRedirectBanner from '../components/InfoHubRedirectBanner';
import DeprecationWarningBanner from '../components/DeprecationWarningBanner';
import getInfoHubUrl from '../utils/get-infohub-url';
import mixpanel from '../utils/mixpanel';
import SideNav from './SideNav';
import SiteSummaryCard from './SiteSummaryCard';

const SitePage = ({ loading, data }) => {
  const { prospectId } = useParams();
  const location = useLocation();
  const { isViewer, isUser } = useUserAuthInfo();
  const searchParams = useSearchParams();
  const [currentLastDueDateUpdate, setCurrentLastDueDateUpdate] = useState();

  const hideNav = searchParams.hideNav === 'true';
  const {
    hideSiteDetailsContent,
    hideServiceContractContent,
    hideCaseActivityContent,
    hideSystemContent,
    hideDocumentsContent,
    hideHomeEnergyReportContent,
  } = useHideFlags();
  const { depWarnServiceContract, depWarnCaseActivity, depWarnSystem, depWarnDocuments, depWarnHomeEnergyReport } =
    useDepWarnFlags();
  const serviceContractInfoHubUrl = getInfoHubUrl(`prospect/${prospectId}`);
  const serviceContractBannerTitle = location.pathname.endsWith('/service-contract/promotions')
    ? 'Service Contract and Promotions'
    : 'Service Contract';
  const scn = data?.prospect?.serviceContractNumber;
  const currentOwnerContactId = data?.prospect?.currentOwner?.contactId;
  const isVivintSolarPostPTO = !!data?.prospect?.isVivintSolarPostPTO;
  const PTODate = data?.prospect?.PTO;

  // Either PTO is undefined OR PTO date less than 100 days
  const meetsPrePTORequirements = () => {
    if (!PTODate) {
      return true;
    }

    const millisecondsIn100Days = 100 * 24 * 60 * 60 * 1000;
    const PTODateLessThan100DaysAgo = new Date(data?.prospect?.PTO) > new Date(Date.now() - millisecondsIn100Days);
    return PTODateLessThan100DaysAgo;
  };
  const isPrePTO = meetsPrePTORequirements();

  useLayoutEffect(() => {
    const fieldsToRegister = { prospectId };
    if (scn) fieldsToRegister['Service Contract Number'] = scn;
    mixpanel.register(fieldsToRegister);
    return () => {
      mixpanel.unregister('prospectId');
      mixpanel.unregister('Service Contract Number');
    };
  }, [prospectId, scn]);

  return (
    <div css={{ display: 'flex' }}>
      {!hideNav && <Header />}
      {!hideNav && <SideNav isVivintSolarPostPTO={isVivintSolarPostPTO} isPrePTO={isPrePTO} />}
      <main css={{ flexGrow: 1, height: '100vh', overflow: 'auto' }}>
        {!hideNav && (
          <div
            css={(theme) => ({
              height: `calc(${theme.mixins.toolbar.minHeight}px + 60px)`, // AppBar + Box height
            })}
          />
        )}
        <Container maxWidth="xl" css={(theme) => ({ paddingTop: theme.spacing(4), paddingBottom: theme.spacing(4) })}>
          {!hideNav && <SiteSummaryCard />}
          <Switch>
            <Route path={SITE_DETAILS_PATH}>
              {hideSiteDetailsContent ? (
                <InfoHubRedirectBanner title="Site Details" infohubUrl={getInfoHubUrl(`prospect/${prospectId}`)} />
              ) : (
                <SiteDetailsPage currentOwnerContactId={currentOwnerContactId} loading={loading} />
              )}
            </Route>
            <Route path={SERVICE_CONTRACT_PATH}>
              {hideServiceContractContent ? (
                <InfoHubRedirectBanner title={serviceContractBannerTitle} infohubUrl={serviceContractInfoHubUrl} />
              ) : (
                <>
                  {depWarnServiceContract && (
                    <DeprecationWarningBanner
                      title={serviceContractBannerTitle}
                      infohubUrl={serviceContractInfoHubUrl}
                    />
                  )}
                  <ServiceContractPage />
                </>
              )}
            </Route>
            <Route path={ACTIVITY_PATH}>
              {hideCaseActivityContent ? (
                <InfoHubRedirectBanner
                  title="Case Activity"
                  infohubUrl={getInfoHubUrl(`prospect/${prospectId}/case-activity`)}
                />
              ) : (
                <>
                  {depWarnCaseActivity && (
                    <DeprecationWarningBanner
                      title="Case Activity"
                      infohubUrl={getInfoHubUrl(`prospect/${prospectId}/case-activity`)}
                    />
                  )}
                  <ActivityPage />
                </>
              )}
            </Route>
            {isViewer && isVivintSolarPostPTO && (
              <Route path={LD_NOTES_PATH} exact>
                <LDNotesPage />
              </Route>
            )}
            {isViewer && (
              <Route path={BILLING_CONTACT_PATH}>
                <BillingPage
                  currentLastDueDateUpdate={currentLastDueDateUpdate}
                  setCurrentLastDueDateUpdate={setCurrentLastDueDateUpdate}
                />
              </Route>
            )}
            {isViewer && (
              <Route path={BILLING_PATH}>
                {currentOwnerContactId ? (
                  <Redirect
                    to={{
                      pathname: pathToRoute(BILLING_CONTACT_PATH, { prospectId, contactId: currentOwnerContactId }),
                      search: hideNav ? '?hideNav=true' : '',
                    }}
                  />
                ) : loading ? (
                  <CircularProgress css={{ margin: 'auto', display: 'block' }} />
                ) : (
                  <Typography align="center">No Billing information found</Typography>
                )}
              </Route>
            )}
            <Route path={SYSTEM_PATH}>
              <SystemPage hideSystemContent={hideSystemContent} depWarnSystem={depWarnSystem} />
            </Route>
            <Route path={DOCUMENTS_PATH}>
              {hideDocumentsContent ? (
                <InfoHubRedirectBanner
                  title="Documents"
                  infohubUrl={getInfoHubUrl(`prospect/${prospectId}/documents`)}
                />
              ) : (
                <>
                  {depWarnDocuments && (
                    <DeprecationWarningBanner
                      title="Documents"
                      infohubUrl={getInfoHubUrl(`prospect/${prospectId}/documents`)}
                    />
                  )}
                  <DocumentsPage />
                </>
              )}
            </Route>
            <Route path={PAYMENT_PATH}>
              <PaymentPage setCurrentLastDueDateUpdate={setCurrentLastDueDateUpdate} />
            </Route>
            <Route path={HOME_ENERGY_REPORT_LIST_PATH}>
              {hideHomeEnergyReportContent ? (
                <InfoHubRedirectBanner title="Home Energy Report" infohubUrl={getInfoHubUrl()} />
              ) : (
                <>
                  {depWarnHomeEnergyReport && (
                    <DeprecationWarningBanner title="Home Energy Report" infohubUrl={getInfoHubUrl()} />
                  )}
                  <HomeEnergyReportListPage />
                </>
              )}
            </Route>
            <Route path={HOME_ENERGY_REPORT_CONTACT_PATH}>
              {hideHomeEnergyReportContent ? (
                <InfoHubRedirectBanner title="Home Energy Report" infohubUrl={getInfoHubUrl()} />
              ) : (
                <>
                  {depWarnHomeEnergyReport && (
                    <DeprecationWarningBanner title="Home Energy Report" infohubUrl={getInfoHubUrl()} />
                  )}
                  <HomeEnergyReportPage />
                </>
              )}
            </Route>
            <Route path={HOME_ENERGY_REPORT_PATH}>
              {hideHomeEnergyReportContent ? (
                <InfoHubRedirectBanner title="Home Energy Report" infohubUrl={getInfoHubUrl()} />
              ) : isUser && currentOwnerContactId ? (
                <Redirect
                  to={pathToRoute(HOME_ENERGY_REPORT_CONTACT_PATH, {
                    prospectId,
                    contactId: currentOwnerContactId,
                  })}
                />
              ) : loading ? (
                <CircularProgress css={{ margin: 'auto', display: 'block' }} />
              ) : (
                <Typography align="center">
                  No contact associated with this prospect, unable to create Home Energy Report.
                </Typography>
              )}
            </Route>
            <Redirect to={pathToRoute(SITE_DETAILS_PATH, { prospectId })} />
          </Switch>
        </Container>
      </main>
    </div>
  );
};

SitePage.propTypes = {
  data: PropTypes.shape({
    prospect: PropTypes.shape({
      serviceContractNumber: PropTypes.string,
      isVivintSolarPostPTO: PropTypes.bool,
      currentOwner: PropTypes.shape({
        contactId: PropTypes.string,
      }),
      PTO: PropTypes.string,
    }),
  }),
  loading: PropTypes.bool,
};

export default SitePage;
